import {
  HotelDestinationSchema,
  HotelResultsFormState,
} from '@reward-platform/ancillaries-schemas/hotel'
import { z } from 'zod'
import { isClient } from '~/utils/envChecks'
import {
  sanitizeBrowserIdentifier,
  sanitizeBrowserText,
} from '~/utils/security/sanitizeBrowserInput'

const HOTEL_DESTINATION_HANDOFF_SESSION_KEY = 'hotels_destination_handoff'
const HANDOFF_TTL_MS = 5 * 60 * 1000

const HotelDestinationSignatureSchema = z.object({
  destSearch: z.string(),
  destId: z.string(),
  destType: z.string(),
})

type HotelDestinationSignature = z.infer<typeof HotelDestinationSignatureSchema>

const HotelDestinationHandoffPayloadSchema = z.object({
  destination: HotelDestinationSchema,
  signature: HotelDestinationSignatureSchema,
  timestamp: z.number(),
})

type HotelDestinationHandoffPayload = z.infer<typeof HotelDestinationHandoffPayloadSchema>

const normalize = (value: string) => value.trim().toLowerCase()

const sanitizeDestination = (
  destination: HotelResultsFormState['destination']
): HotelResultsFormState['destination'] => ({
  ...destination,
  id: sanitizeBrowserIdentifier(destination.id, 64),
  type: sanitizeBrowserIdentifier(destination.type, 64),
  fullName: sanitizeBrowserText(destination.fullName, 160),
  name: destination.name ? sanitizeBrowserText(destination.name, 160) : destination.name,
})

const createHotelDestinationSignature = ({
  destSearch,
  destId,
  destType,
}: HotelDestinationSignature): HotelDestinationSignature => ({
  destSearch: normalize(destSearch),
  destId: normalize(destId),
  destType: normalize(destType),
})

const readHotelDestinationHandoff = (): HotelDestinationHandoffPayload | null => {
  if (isClient()) {
    const rawData = sessionStorage.getItem(HOTEL_DESTINATION_HANDOFF_SESSION_KEY)
    if (!rawData) {
      return null
    }

    try {
      const result = HotelDestinationHandoffPayloadSchema.safeParse(JSON.parse(rawData))
      if (!result.success) {
        sessionStorage.removeItem(HOTEL_DESTINATION_HANDOFF_SESSION_KEY)
        return null
      }
      return result.data
    } catch {
      sessionStorage.removeItem(HOTEL_DESTINATION_HANDOFF_SESSION_KEY)
    }
  }
  return null
}

const isHandoffExpired = (timestamp: number): boolean => Date.now() - timestamp > HANDOFF_TTL_MS

export const setHotelDestinationHandoff = ({
  destination,
  signature,
}: Pick<HotelDestinationHandoffPayload, 'destination' | 'signature'>): void => {
  if (isClient()) {
    const payload: HotelDestinationHandoffPayload = {
      destination,
      signature: createHotelDestinationSignature(signature),
      timestamp: Date.now(),
    }

    sessionStorage.setItem(HOTEL_DESTINATION_HANDOFF_SESSION_KEY, JSON.stringify(payload))
  }
}

export const clearHotelDestinationHandoff = (): void => {
  if (isClient()) {
    sessionStorage.removeItem(HOTEL_DESTINATION_HANDOFF_SESSION_KEY)
  }
}

export const consumeHotelDestinationHandoff = (
  signature: HotelDestinationSignature
): HotelResultsFormState['destination'] | null => {
  const payload = readHotelDestinationHandoff()
  if (!payload) {
    return null
  }

  if (isHandoffExpired(payload.timestamp)) {
    clearHotelDestinationHandoff()
    return null
  }

  const normalizedSignature = createHotelDestinationSignature(signature)
  const isIdAndTypeMatch =
    payload.signature.destId === normalizedSignature.destId &&
    payload.signature.destType === normalizedSignature.destType

  if (!isIdAndTypeMatch) {
    return null
  }

  const sanitizedDestination = sanitizeDestination(payload.destination)
  if (!sanitizedDestination.fullName || !sanitizedDestination.id || !sanitizedDestination.type) {
    return null
  }

  // Keep this entry until expiry/overwrite to survive strict-mode remounts.
  // `dest_search` match is intentionally non-blocking because URL text may be normalized.
  return sanitizedDestination
}
